import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {alpha} from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import Iconify from '../../theme/iconify/iconify';

import NewCorpus from './NewCorpus';
import CorpusCard from './CorpusCard';
import Notification from '../util/Notification';
import {mislecAPI} from "../../util/axios";

function Corpus({ user, updateUser }) {
    const [corpuses, setCorpuses] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);
    const [corpusModal, setCorpusModal] = React.useState(false);
    const [fetched, setFetched] = React.useState(false);

    const { t } = useTranslation();
    const navigate = useNavigate();

    React.useEffect(() => {
        fetchCorpuses();
    }, [user]);

    const createCorpus = (title, documents) => {
        if (title === '') {
            setOpen(true);
            setMessage(t('corpuses.modals.new.error.title'));
            setSeverity('warning');
            return;
        }

        //  Timestamp
        const timestamp = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');

        //  Request body
        const data = {
            title: title,
            user: user._id,
            timestamp: timestamp,
            documents: documents.map((document) => document._id)
        }

        //  Send data to server
        mislecAPI.post(`/api/corpus`, data)
            .then(res => {
                //  Fetch corpuses
                fetchCorpuses();

                //  Notify user of success
                setOpen(true);
                setMessage(t(res.data.detail));
                setSeverity('success');
                setCorpusModal(false);

                setTimeout(() => {
                    //  Redirect to the new chat
                    navigate(`/corpus/${res.data.id}`);
                }, 2500);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    const fetchCorpuses = async () => {
        if (!user) return;

        //  Fetch documents from the server
        mislecAPI.get(`/api/corpus`)
            .then(res => {
                setCorpuses(res.data.corpuses);
                setFetched(true);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    if (!fetched) {
        fetchCorpuses();
    }

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={5}>
                    <Typography variant='h4'>{t('corpuses.title')}</Typography>

                    <Button
                        variant=''
                        startIcon={<Iconify icon='mdi:folder-plus-outline' />}
                        onClick={() => setCorpusModal(true)}
                        sx={{
                            borderRadius: '8px !important',
                            backgroundColor: 'rgba(33, 43, 54, 0.08)',
                            '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main'
                            }
                        }}
                    >
                        {t('corpuses.buttons.new')}
                    </Button>
                </Stack>

                <Stack mb={5}>
                    <Grid container spacing={3}>
                        {corpuses.length > 0 ? corpuses.map((corpus, index) => (
                            <CorpusCard key={index} corpus={corpus} />
                        )) :
                            <Grid item xs={12}>
                                <Typography variant='body1'>{t('corpuses.noCorpuses')}</Typography>
                            </Grid>
                        }
                    </Grid>
                </Stack>
            </Container>
            <NewCorpus user={user} updateUser={updateUser} createCorpus={createCorpus} corpusModal={corpusModal} setCorpusModal={setCorpusModal} setOpen={setOpen} setMessage={setMessage} setSeverity={setSeverity} />
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Corpus;